/**
 * CapabilityUpsellTab — Editor contenuti upsell per capability.
 *
 * Per ogni capability (module o feature) mostra editor di:
 *  - label (display name)
 *  - description (testo breve)
 *  - value_pitch (testo lungo per UpgradeRequiredCard / UpgradeDialog)
 *  - value_pitch_short (testo breve per tooltip / variant compact)
 *  - upsell_image_url (link a immagine illustrativa)
 *
 * Salva live a Firestore. Le componenti upsell propagano senza redeploy.
 *
 * UI: lista ricercabile + form a destra del capability selezionato.
 *
 * @since 2026-05-18
 */

import React, { useMemo, useState } from 'react';
import {
  Autocomplete, Box, Stack, Typography, TextField, Button, Chip, InputAdornment,
  CircularProgress, alpha,
} from '@mui/material';
import { Save, Check, Search, Lock as LockIcon } from '@mui/icons-material';
import { tokens } from '../../theme';
import type { CapabilityDoc } from '../../routes/PricingPage';

const TIER_COLOR_MAP: Record<string, string> = {
  // Il piano d'ingresso si chiama 'base' dal 19/05/2026 (prima 'free'): senza questa voce la linguetta andava in
  // errore appena una capability del Base compariva nell'elenco.
  base: tokens.color.tierFree.main,
  free: tokens.color.tierFree.main,
  pro: tokens.color.tierPro.main,
  business: tokens.color.tierBusiness.main,
};

interface CapabilityUpsellTabProps {
  capabilities: CapabilityDoc[];
  /** capability_id → tier minimo che la include (free/pro/business). */
  capabilityToTier: Record<string, string>;
  /** tierId → display label */
  tierLabelMap: Record<string, string>;
  /** I prodotti che le aziende dichiarano di vendere (scelte per «Per quali prodotti»). */
  prodottiConosciuti: string[];
  onUpdate: (cap: CapabilityDoc, patch: Partial<CapabilityDoc>) => Promise<void>;
}

export const CapabilityUpsellTab: React.FC<CapabilityUpsellTabProps> = ({
  capabilities,
  capabilityToTier,
  tierLabelMap,
  prodottiConosciuti,
  onUpdate,
}) => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(capabilities[0]?.id ?? null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return capabilities;
    return capabilities.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q),
    );
  }, [capabilities, search]);

  const selected = capabilities.find((c) => c.id === selectedId) ?? null;

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minHeight: 480 }}>
      {/* COLONNA SINISTRA — lista */}
      <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Cerca capability…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: tokens.color.mist }} />
              </InputAdornment>
            ),
            sx: { fontSize: '0.85rem' },
          }}
          sx={{ mb: 1.5 }}
        />
        <Box
          sx={{
            border: `1px solid ${tokens.color.line}`,
            borderRadius: 2,
            maxHeight: 560,
            overflow: 'auto',
          }}
        >
          {filtered.map((c) => {
            const tier = capabilityToTier[c.id];
            const tierColor = (tier ? TIER_COLOR_MAP[tier] : undefined) ?? tokens.color.mist;
            const isSelected = c.id === selectedId;
            const missingPitch = !c.value_pitch?.trim();
            return (
              <Box
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderBottom: `1px solid ${tokens.color.lineSoft}`,
                  cursor: 'pointer',
                  bgcolor: isSelected ? alpha(tokens.color.graphite, 0.04) : 'transparent',
                  borderLeft: `3px solid ${isSelected ? tierColor : 'transparent'}`,
                  transition: 'background-color 120ms',
                  '&:hover': { bgcolor: alpha(tokens.color.graphite, 0.025) },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, flex: 1, color: tokens.color.graphite }}>
                    {c.label}
                  </Typography>
                  {tier && (
                    <Chip
                      label={tierLabelMap[tier] ?? tier}
                      size="small"
                      sx={{
                        bgcolor: alpha(tierColor, 0.12),
                        color: tierColor,
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        height: 18,
                        letterSpacing: '0.04em',
                      }}
                    />
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography
                    sx={{
                      fontFamily: tokens.font.mono,
                      fontSize: '0.66rem',
                      color: tokens.color.mist,
                    }}
                  >
                    {c.id}
                  </Typography>
                  {missingPitch && (
                    <Chip
                      label="pitch mancante"
                      size="small"
                      sx={{
                        bgcolor: alpha(tokens.color.warning, 0.12),
                        color: tokens.color.warning,
                        fontSize: '0.6rem',
                        height: 16,
                      }}
                    />
                  )}
                </Stack>
              </Box>
            );
          })}
          {filtered.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                Nessuna capability
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* COLONNA DESTRA — editor */}
      <Box sx={{ flex: 1 }}>
        {selected ? (
          <CapabilityEditor
            key={selected.id}
            cap={selected}
            tier={capabilityToTier[selected.id]}
            tierLabel={capabilityToTier[selected.id] ? tierLabelMap[capabilityToTier[selected.id]] : undefined}
            prodottiConosciuti={prodottiConosciuti}
            onUpdate={onUpdate}
          />
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" sx={{ color: tokens.color.mist }}>
              Seleziona una capability dalla lista.
            </Typography>
          </Box>
        )}
      </Box>
    </Stack>
  );
};

interface CapabilityEditorProps {
  cap: CapabilityDoc;
  tier?: string;
  tierLabel?: string;
  prodottiConosciuti: string[];
  onUpdate: (cap: CapabilityDoc, patch: Partial<CapabilityDoc>) => Promise<void>;
}

const stessiProdotti = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && [...a].sort().every((v, i) => v === [...b].sort()[i]);

const CapabilityEditor: React.FC<CapabilityEditorProps> = ({ cap, tier, tierLabel, prodottiConosciuti, onUpdate }) => {
  const [label, setLabel] = useState(cap.label ?? '');
  const [description, setDescription] = useState(cap.description ?? '');
  const [pitch, setPitch] = useState(cap.value_pitch ?? '');
  const [pitchShort, setPitchShort] = useState(cap.value_pitch_short ?? '');
  const [imageUrl, setImageUrl] = useState(cap.upsell_image_url ?? '');
  const [prodotti, setProdotti] = useState<string[]>(cap.prodotti_pertinenti ?? []);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  const tierColor = (tier ? TIER_COLOR_MAP[tier] : undefined) ?? tokens.color.mist;
  const dirty =
    label !== (cap.label ?? '') ||
    description !== (cap.description ?? '') ||
    pitch !== (cap.value_pitch ?? '') ||
    pitchShort !== (cap.value_pitch_short ?? '') ||
    imageUrl !== (cap.upsell_image_url ?? '') ||
    !stessiProdotti(prodotti, cap.prodotti_pertinenti ?? []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(cap, {
        label: label.trim(),
        description: description.trim(),
        value_pitch: pitch.trim(),
        value_pitch_short: pitchShort.trim(),
        upsell_image_url: imageUrl.trim() || null,
        prodotti_pertinenti: [...prodotti].sort(),
      });
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography
          sx={{
            fontFamily: tokens.font.mono,
            fontSize: '0.72rem',
            color: tokens.color.mist,
          }}
        >
          {cap.id}
        </Typography>
        <Chip
          label={cap.type}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.62rem', height: 18, color: tokens.color.ash, borderColor: tokens.color.line }}
        />
        {tier && tierLabel && (
          <Chip
            icon={<LockIcon sx={{ fontSize: 12 }} />}
            label={`Incluso da ${tierLabel}`}
            size="small"
            sx={{
              bgcolor: alpha(tierColor, 0.12),
              color: tierColor,
              fontSize: '0.66rem',
              fontWeight: 700,
              height: 20,
              '& .MuiChip-icon': { color: tierColor },
            }}
          />
        )}
      </Stack>

      <Autocomplete
        multiple
        options={prodottiConosciuti}
        value={prodotti}
        onChange={(_, v) => setProdotti(v)}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Per quali prodotti ha senso"
            helperText="Vuoto = per tutte le aziende. Con uno o più prodotti compare solo alle aziende che ne vendono almeno uno (i loro «Prodotti disponibili»). Un'aggiunta o un'eccezione decisa a mano vince sempre. Al salvataggio le aziende si ricalcolano da sole."
          />
        )}
      />
      <TextField
        label="Display name (label)"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        size="small"
        fullWidth
        helperText="Nome mostrato nella UI utente"
      />
      <TextField
        label="Descrizione breve (description)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        size="small"
        fullWidth
        multiline
        minRows={2}
        helperText="1-2 frasi, usata come fallback se manca il value_pitch"
      />
      <TextField
        label="Value pitch (lungo, per la card upsell)"
        value={pitch}
        onChange={(e) => setPitch(e.target.value)}
        size="small"
        fullWidth
        multiline
        minRows={3}
        helperText="Mostrato dentro UpgradeRequiredCard e UpgradeDialog. 2-4 frasi persuasive."
      />
      <TextField
        label="Value pitch breve (short, per tooltip/badge)"
        value={pitchShort}
        onChange={(e) => setPitchShort(e.target.value)}
        size="small"
        fullWidth
        helperText="Una frase per tooltip o variant compact"
      />

      <Stack spacing={1}>
        <TextField
          label="URL immagine upsell"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          size="small"
          fullWidth
          placeholder="https://… (URL pubblico, es. da Firebase Storage)"
          helperText="Immagine illustrativa (screenshot / mockup) mostrata nella card upsell. Ratio 16:9 consigliato."
        />
        {imageUrl && (
          <Box
            sx={{
              border: `1px solid ${tokens.color.line}`,
              borderRadius: 2,
              overflow: 'hidden',
              maxWidth: 420,
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt="anteprima"
              sx={{ width: '100%', display: 'block', maxHeight: 240, objectFit: 'cover' }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </Box>
        )}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={savedTick ? <Check /> : <Save />}
          onClick={handleSave}
          disabled={!dirty || saving}
          sx={{
            bgcolor: tierColor,
            '&:hover': { bgcolor: tierColor, filter: 'brightness(0.92)' },
          }}
        >
          {saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : savedTick ? 'Salvato' : 'Salva'}
        </Button>
        {dirty && !saving && (
          <Typography variant="caption" sx={{ color: tokens.color.warning, fontWeight: 600 }}>
            Modifiche non salvate
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};
